import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { getCountryOptions, SELLER_COUNTRY_CODES } from '../../../util/shipping';
import { submitExpansionLead } from '../../../util/api';
import * as validators from '../../../util/validators';
import {
  Button,
  FieldCheckbox,
  FieldSelect,
  FieldTextInput,
  Form,
  H4,
  Modal,
  NamedLink,
} from '../../../components';

import css from './ExpansionLeadModal.module.css';

const consentChecked = value =>
  value === true || value === 'yes' || (Array.isArray(value) && value.includes('yes'));

/**
 * Modal form for sellers whose country is not yet supported.
 * Submits country to /api/expansion-lead; the server uses the logged-in user's email.
 *
 * @component
 */
const ExpansionLeadModal = props => {
  const {
    id = 'ProfileSettingsForm.expansionLeadModal',
    isOpen,
    onClose,
    onManageDisableScrolling,
    initialEmail = '',
  } = props;
  const intl = useIntl();
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const supportedCodes = new Set(SELLER_COUNTRY_CODES.map(code => `${code}`.toUpperCase()));
  const countryOptions = getCountryOptions(intl.locale).filter(
    country => !supportedCodes.has(country.code)
  );
  const countryRequired = validators.required(
    intl.formatMessage({ id: 'ExpansionLeadModal.countryRequired' })
  );
  const consentRequiredMessage = intl.formatMessage({
    id: 'ExpansionLeadModal.consentRequired',
  });
  const consentRequired = value => (consentChecked(value) ? undefined : consentRequiredMessage);

  const handleClose = () => {
    setSubmitError(null);
    setSubmitted(false);
    onClose();
  };

  const handleSubmit = async values => {
    setSubmitError(null);
    const country = `${values.country || ''}`.toUpperCase();
    try {
      await submitExpansionLead({ country });
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e);
    }
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.root}>
        {submitted ? (
          <>
            <H4 as="h2" className={css.title}>
              <FormattedMessage id="ExpansionLeadModal.successTitle" />
            </H4>
            <p className={css.message}>
              <FormattedMessage id="ExpansionLeadModal.successMessage" />
            </p>
            <Button className={css.submitButton} type="button" onClick={handleClose}>
              <FormattedMessage id="ExpansionLeadModal.close" />
            </Button>
          </>
        ) : (
          <>
            <H4 as="h2" className={css.title}>
              <FormattedMessage id="ExpansionLeadModal.title" />
            </H4>
            <p className={css.message}>
              <FormattedMessage id="ExpansionLeadModal.description" />
            </p>
            <FinalForm
              onSubmit={handleSubmit}
              initialValues={{ email: initialEmail, country: '' }}
              render={formRenderProps => {
                const { handleSubmit: formHandleSubmit, invalid, submitting } = formRenderProps;
                const submitDisabled = invalid || submitting;

                return (
                  <Form className={css.form} onSubmit={formHandleSubmit}>
                    <FieldSelect
                      className={css.field}
                      id={`${id}.country`}
                      name="country"
                      label={intl.formatMessage({ id: 'ExpansionLeadModal.countryLabel' })}
                      validate={countryRequired}
                    >
                      <option disabled value="">
                        {intl.formatMessage({ id: 'ExpansionLeadModal.countryPlaceholder' })}
                      </option>
                      {countryOptions.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </FieldSelect>
                    <FieldTextInput
                      className={css.field}
                      type="email"
                      id={`${id}.email`}
                      name="email"
                      label={intl.formatMessage({ id: 'ExpansionLeadModal.emailLabel' })}
                      placeholder={intl.formatMessage({
                        id: 'ExpansionLeadModal.emailPlaceholder',
                      })}
                      disabled
                    />
                    <FieldCheckbox
                      className={css.consent}
                      id={`${id}.consentLaunchNotify`}
                      name="consentLaunchNotify"
                      value="yes"
                      label={intl.formatMessage({ id: 'ExpansionLeadModal.consentLabel' })}
                      validate={consentRequired}
                    />
                    <p className={css.consentNote}>
                      <FormattedMessage
                        id="ExpansionLeadModal.privacyNote"
                        values={{
                          privacyPolicyLink: (
                            <NamedLink name="PrivacyPolicyPage" className={css.privacyLink}>
                              <FormattedMessage id="ExpansionLeadModal.privacyPolicyLink" />
                            </NamedLink>
                          ),
                        }}
                      />
                    </p>
                    {submitError ? (
                      <p className={classNames(css.error)} role="alert">
                        <FormattedMessage id="ExpansionLeadModal.submitFailed" />
                      </p>
                    ) : null}
                    <Button
                      className={css.submitButton}
                      type="submit"
                      inProgress={submitting}
                      disabled={submitDisabled}
                    >
                      <FormattedMessage id="ExpansionLeadModal.submit" />
                    </Button>
                  </Form>
                );
              }}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExpansionLeadModal;
